import { createResource, createSignal, For, Show } from "solid-js";
import type { MeasurementPoint } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatNumber } from "../format.ts";
import { pathTo, reloadTree, siteTree } from "../context.ts";
import { familyOf } from "../supply.ts";
import { ErrorBox } from "./ErrorBox.tsx";
import { operator, useAction } from "./ui.tsx";

/**
 * Where a meter gets its electricity, water, gas or air from (supply topology, apart from the place tree): its normal
 * supply, the backup an automatic transfer switch moves it to, for an electricity grid connection its capacity and for
 * a utility meter on a pulse input what one impulse is worth. Without a normal supply set, the host derives it from
 * the place tree when that is unambiguous. Supplies stay within the meter's medium.
 */
export function SupplyPanel(props: {
  point: MeasurementPoint;
  onChanged: () => Promise<void>;
}) {
  const root = () => pathTo(siteTree()?.nodes ?? [], props.point.nodeId)[0]?.id;
  const family = () => familyOf(props.point) ?? "el";
  const utility = () => family() !== "el";
  const [links, { refetch }] = createResource(
    () => (root() ? { id: root()!, family: family() } : undefined),
    (q) => api.sites.supply(q.id, q.family === "el" ? undefined : q.family),
  );
  const link = () => links()?.find((l) => l.pointId === props.point.id);
  const isMain = () => props.point.role === "main";
  const others = () =>
    (siteTree()?.points ?? [])
      .filter((p) => p.id !== props.point.id && familyOf(p) === family())
      .sort((a, b) => a.name.localeCompare(b.name));
  const nameOf = (id: string | null | undefined) =>
    (siteTree()?.points ?? []).find((p) => p.id === id)?.name ?? "";
  const [feed, setFeed] = createSignal(props.point.feedFrom ?? "");
  const [backup, setBackup] = createSignal(props.point.backupFrom ?? "");
  const [capacity, setCapacity] = createSignal(
    props.point.capacityKw === null ? "" : String(props.point.capacityKw),
  );
  const [weight, setWeight] = createSignal(
    props.point.unitsPerPulse === null ? "" : String(props.point.unitsPerPulse),
  );
  const [run, error, busy] = useAction();

  const save = (e: SubmitEvent) => {
    e.preventDefault();
    void run(async () => {
      const number = (v: string) =>
        v.trim() === "" ? null : Number(v.replace(",", "."));
      await api.sites.setSupply(props.point.id, {
        feedFrom: feed() || null,
        backupFrom: backup() || null,
        capacityKw: utility() ? null : number(capacity()),
      });
      if (utility()) {
        await api.sites.setPulseWeight(props.point.id, number(weight()));
      }
      await reloadTree();
      await refetch();
      await props.onChanged();
    });
  };

  return (
    <form class="stack" onSubmit={save}>
      <Show when={utility()}>
        <div class="field">
          <label for="supplyWeight">{t("supply.pulseWeight")}</label>
          <input
            id="supplyWeight"
            inputmode="decimal"
            placeholder="0,01"
            value={weight()}
            disabled={!operator()}
            onInput={(e) => setWeight(e.currentTarget.value)}
          />
          <p class="hint">{t("supply.pulseWeightHint")}</p>
        </div>
      </Show>
      <Show when={isMain() && !utility()}>
        <div class="field">
          <label for="supplyCapacity">{t("supply.capacity")}</label>
          <input
            id="supplyCapacity"
            inputmode="decimal"
            value={capacity()}
            disabled={!operator()}
            onInput={(e) => setCapacity(e.currentTarget.value)}
          />
          <p class="hint">{t("supply.capacityHint")}</p>
        </div>
      </Show>
      <Show when={!isMain()}>
        <dl class="kv">
          <dt>{t("supply.now")}</dt>
          <dd>
            <Show when={link()} fallback="—">
              {(l) => (
                <Show
                  when={!l().unassigned}
                  fallback={
                    <span class="chip warn">{t("supply.unassigned")}</span>
                  }
                >
                  {nameOf(l().parentId)}
                  <span class="hint">
                    {" "}
                    ·{" "}
                    {l().explicit ? t("supply.explicit") : t("supply.derived")}
                  </span>
                  <Show when={l().backupId}>
                    <div class="hint">
                      {t("supply.backupOf", { name: nameOf(l().backupId) })}
                    </div>
                  </Show>
                </Show>
              )}
            </Show>
          </dd>
        </dl>
        <div class="field">
          <label for="supplyFeed">{t("supply.feedFrom")}</label>
          <select
            id="supplyFeed"
            value={feed()}
            disabled={!operator()}
            onChange={(e) => setFeed(e.currentTarget.value)}
          >
            <option value="">{t("supply.automatic")}</option>
            <For each={others()}>
              {(p) => (
                <option
                  value={p.id}
                >{`${p.name} · ${t(`role.point.${p.role}`)}`}</option>
              )}
            </For>
          </select>
          <p class="hint">{t("supply.feedHint")}</p>
        </div>
        <div class="field">
          <label for="supplyBackup">{t("supply.backupFrom")}</label>
          <select
            id="supplyBackup"
            value={backup()}
            disabled={!operator()}
            onChange={(e) => setBackup(e.currentTarget.value)}
          >
            <option value="">{t("supply.noBackup")}</option>
            <For each={others()}>
              {(p) => (
                <option
                  value={p.id}
                >{`${p.name} · ${t(`role.point.${p.role}`)}`}</option>
              )}
            </For>
          </select>
          <p class="hint">{t("supply.backupHint")}</p>
        </div>
      </Show>
      <Show when={isMain() && !utility() && props.point.capacityKw !== null}>
        <p class="hint" style={{ margin: 0 }}>
          {t("supply.capacityNow", {
            kw: formatNumber(props.point.capacityKw, locale(), 0),
          })}
        </p>
      </Show>
      <Show when={error()}>
        <ErrorBox error={error()} />
      </Show>
      <Show when={operator()}>
        <div class="row-acts" style={{ "justify-content": "flex-end" }}>
          <button type="submit" class="btn primary" disabled={busy()}>
            {t("common.save")}
          </button>
        </div>
      </Show>
    </form>
  );
}
